import React, { createContext, useContext, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  const loadPermissions = async () => {
    try {
      const { data } = await api.get("/permissions/me");
      setPermissions(data.permissions || {});
    } catch {
      setPermissions({});
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("ro_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then(async (r) => {
        setUser(r.data);
        await loadPermissions();
      })
      .catch(() => localStorage.removeItem("ro_token"))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("ro_token", data.token);
      setUser(data.user);
      await loadPermissions();
      toast.success(`Welcome, ${data.user.full_name}`);
      return true;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Login failed");
      return false;
    }
  };

  const startSession = async (data) => {
    localStorage.setItem("ro_token", data.token);
    setUser(data.user);
    await loadPermissions();
  };

  const logout = () => {
    const tok = localStorage.getItem("ro_token");
    if (tok) api.post("/auth/logout", null, { headers: { Authorization: `Bearer ${tok}` } }).catch(() => {});
    localStorage.removeItem("ro_token");
    setUser(null);
    setPermissions({});
  };

  const can = (key) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    return !!permissions[key];
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, startSession, permissions, can, refreshPermissions: loadPermissions }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
